import { getCleverReachListId, CleverReachList, subscribeToNewsletter } from '@cineamo/newsletter';
import { useTranslations } from 'next-intl';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import Button from '@/components/button/Button';

import { DomainConfigurationDto } from '@/types/DomainConfigurationDto';

import { ID_INNENSTADT_KINOS } from '@cineamo/api/constants/cinema-ids';

import { withOpacity } from '@cineamo/ui/src/helpers/color-helper';
import { showToast } from '@cineamo/ui/src/helpers/toast-helper';

import { IconLoading16 } from '@cineamo/ui/src/icons/ic-loading';
import { IconSuccessful16 } from '@cineamo/ui/src/icons/ic-successful';

import { track } from '@/utils/server-side-tracking';

import FormCheckbox from './FormCheckbox';
import FormInput from './FormInput';
import { sendEmailKidsNTeens } from './send-email';
import TurnstileWidget from './TurnstileWidget';

export type KidsNTeensFormData = {
    firstName: string;
    lastName: string;
    street: string;
    houseNumber: string;
    postalCode: string;
    city: string;
    email: string;
    phone: string;
    birthDate: string;
    newsletterConsent: boolean;
    privacyConsent: boolean;
};

type KidsNTeensFormProps = {
    domainConfiguration: DomainConfigurationDto;
};

export default function KidsNTeensForm({ domainConfiguration }: KidsNTeensFormProps) {
    const t = useTranslations();
    const [isProcessing, setIsProcessing] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

    const isCinemaInnenstadtkinos = domainConfiguration.cinema?.id === ID_INNENSTADT_KINOS;

    // Check if Kids & Teens newsletter is available for this cinema
    const hasKidsTeensNewsletter = domainConfiguration.cinema?.id
        ? getCleverReachListId(domainConfiguration.cinema.id, CleverReachList.kids_teens) !== null
        : false;

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        reset,
        formState: { errors }
    } = useForm<KidsNTeensFormData>({
        defaultValues: {
            firstName: '',
            lastName: '',
            street: '',
            houseNumber: '',
            postalCode: '',
            city: '',
            email: '',
            phone: '',
            birthDate: '',
            newsletterConsent: isCinemaInnenstadtkinos,
            privacyConsent: false
        }
    });

    const newsletterConsent = watch('newsletterConsent');
    const privacyConsent = watch('privacyConsent');

    // Register privacyConsent with validation
    React.useEffect(() => {
        register('privacyConsent', { required: t('kids-n-teens.validation.privacy-required') });
    }, [register, t]);

    const onSubmit = async (data: KidsNTeensFormData) => {
        setIsProcessing(true);

        try {
            const success = await sendEmailKidsNTeens(data, turnstileToken ?? undefined);

            if (success) {
                // Handle newsletter subscription if consented and available
                if (data.newsletterConsent && domainConfiguration.cinema && hasKidsTeensNewsletter) {
                    try {
                        await subscribeToNewsletter(
                            domainConfiguration.cinema,
                            {
                                email: data.email,
                                firstName: data.firstName,
                                lastName: data.lastName,
                                privacyConsent: true // Already validated above
                            },
                            CleverReachList.kids_teens,
                            window.location.href,
                            undefined, // userIP
                            true // skipDOI - activate directly for Kids & Teens
                        );
                    } catch {
                        // Newsletter subscription failed, but don't fail the main form
                        // Silent error handling
                    }
                }

                setIsSuccess(true);
                reset();
                showToast({
                    toastType: toast.success,
                    messageIcon: <IconSuccessful16 />,
                    messageTitle: t('kids-n-teens.success.title'),
                    messageBody: t('kids-n-teens.success.message'),
                    backgroundColor: domainConfiguration.style.primaryColor,
                    textColor: domainConfiguration.style.onPrimaryColor
                });
            } else {
                showToast({
                    toastType: toast.error,
                    messageTitle: t('kids-n-teens.error.title'),
                    messageBody: t('kids-n-teens.error.message')
                });
            }

            track('Kids N Teens Form Submitted', {
                cinema: domainConfiguration.cinema?.name,
                domain: domainConfiguration.domain,
                cinemaId: domainConfiguration.cinema?.id,
                data: JSON.stringify(data, null, 2),
                success: success,
                newsletterConsent: data.newsletterConsent
            });
        } catch {
            showToast({
                toastType: toast.error,
                messageTitle: t('kids-n-teens.error.title'),
                messageBody: t('kids-n-teens.error.network-message')
            });
        } finally {
            setIsProcessing(false);
        }
    };

    if (isSuccess) {
        return (
            <div
                className="backdrop-blur-sm rounded-12 p-12 shadow-lg text-center"
                style={{ backgroundColor: domainConfiguration.style.surfaceColor }}>
                <IconSuccessful16
                    className="size-[48px] mx-auto mb-4"
                    style={{ color: domainConfiguration.style.primaryColor }}
                />
                <h3
                    className="text-headline-24 mb-2"
                    style={{ color: domainConfiguration.style.onBackgroundColor }}>
                    {t('kids-n-teens.success.title')}
                </h3>
                <p
                    className="text-body-16"
                    style={{ color: withOpacity(domainConfiguration.style.onBackgroundColor, 0.8) }}>
                    {t('kids-n-teens.success.message')}
                </p>
            </div>
        );
    }

    return (
        <div
            className="backdrop-blur-sm rounded-12 p-12 shadow-lg"
            style={{ backgroundColor: domainConfiguration.style.surfaceColor }}>
            <form
                className="space-y-24"
                onSubmit={handleSubmit(onSubmit)}>
                {/* Personal Information */}
                <div className="grid md:grid-cols-2 gap-6">
                    <FormInput
                        name="firstName"
                        label={t('kids-n-teens.form.first-name')}
                        type="text"
                        register={register}
                        registerOptions={{ required: true }}
                        required
                        domainConfiguration={domainConfiguration}
                    />
                    <FormInput
                        name="lastName"
                        label={t('kids-n-teens.form.last-name')}
                        type="text"
                        register={register}
                        registerOptions={{ required: true }}
                        required
                        domainConfiguration={domainConfiguration}
                    />
                </div>

                {/* Address Information */}
                <div className="grid md:grid-cols-3 gap-6">
                    <div className="md:col-span-2">
                        <FormInput
                            name="street"
                            label={t('kids-n-teens.form.street')}
                            type="text"
                            register={register}
                            registerOptions={{ required: true }}
                            required
                            domainConfiguration={domainConfiguration}
                        />
                    </div>
                    <FormInput
                        name="houseNumber"
                        label={t('kids-n-teens.form.house-number')}
                        type="text"
                        register={register}
                        registerOptions={{ required: true }}
                        required
                        domainConfiguration={domainConfiguration}
                    />
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                    <FormInput
                        name="postalCode"
                        label={t('kids-n-teens.form.postal-code')}
                        type="text"
                        register={register}
                        registerOptions={{ required: true }}
                        required
                        domainConfiguration={domainConfiguration}
                    />
                    <div className="md:col-span-2">
                        <FormInput
                            name="city"
                            label={t('kids-n-teens.form.city')}
                            type="text"
                            register={register}
                            registerOptions={{ required: true }}
                            required
                            domainConfiguration={domainConfiguration}
                        />
                    </div>
                </div>

                {/* Contact Information */}
                <div className="grid md:grid-cols-2 gap-6">
                    <FormInput
                        name="email"
                        label={t('kids-n-teens.form.email')}
                        type="email"
                        register={register}
                        registerOptions={{ required: true }}
                        required
                        domainConfiguration={domainConfiguration}
                    />
                    <FormInput
                        name="phone"
                        label={t('kids-n-teens.form.phone')}
                        type="tel"
                        register={register}
                        registerOptions={{ required: true }}
                        required
                        domainConfiguration={domainConfiguration}
                    />
                </div>

                {/* Birth Date */}
                <FormInput
                    name="birthDate"
                    label={t('kids-n-teens.form.birth-date')}
                    type="date"
                    register={register}
                    registerOptions={{ required: true }}
                    required
                    domainConfiguration={domainConfiguration}
                />

                {/* Consent Checkboxes */}
                <div className="space-y-4">
                    {hasKidsTeensNewsletter && !isCinemaInnenstadtkinos && (
                        <FormCheckbox
                            name="newsletter-consent"
                            label={t('kids-n-teens.form.newsletter-consent')}
                            checked={newsletterConsent}
                            onChange={(checked) => setValue('newsletterConsent', checked)}
                            domainConfiguration={domainConfiguration}
                        />
                    )}
                    <FormCheckbox
                        name="privacy-consent"
                        label={t('kids-n-teens.form.privacy-consent')}
                        checked={privacyConsent}
                        onChange={(checked) => setValue('privacyConsent', checked)}
                        required
                        error={errors.privacyConsent?.message}
                        domainConfiguration={domainConfiguration}
                    />
                </div>

                {/* Separator */}
                <div
                    className="w-full h-1"
                    style={{
                        backgroundColor: withOpacity(domainConfiguration.style.onSurfaceColor, 0.3)
                    }}
                />

                {/* Required Fields Note */}
                <div className="pt-4">
                    <p
                        className="text-body-12 text-center"
                        style={{
                            color: withOpacity(domainConfiguration.style.onSurfaceColor, 0.7)
                        }}>
                        {t('kids-n-teens.form.required-fields')}
                    </p>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                    <TurnstileWidget onVerify={setTurnstileToken} />
                    <Button
                        type="submit"
                        disabled={isProcessing}
                        className="w-full py-4 text-body-16 font-semibold transition-all duration-200 rounded-8"
                        css={{
                            backgroundColor: domainConfiguration.style.primaryColor,
                            color: domainConfiguration.style.onPrimaryColor,
                            opacity: isProcessing ? 0.6 : 1
                        }}
                        leadingIcon={isProcessing ? <IconLoading16 className="animate-spin" /> : undefined}>
                        {isProcessing ? t('kids-n-teens.form.submitting') : t('kids-n-teens.form.submit')}
                    </Button>
                </div>
            </form>
        </div>
    );
}
